import { Component, OnInit, Inject, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { FileLikeObject, FileItem } from 'ng2-file-upload';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { trim, isNil, replace } from 'lodash';
import { VideosService } from '@app/shared/services/api/videos.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { environment } from '@env/environment';
import { VirusScannerService } from '@app/shared/services/virus-scanner.service';
import { CustomFileUploader } from '@app/shared/custom-file-uploader';
import { StringUtility } from '@app/shared/string-utility';

@Component({
  selector: 'app-video-upload-dialog',
  templateUrl: './video-upload-dialog.component.html',
  styleUrls: ['./video-upload-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VideoUploadDialogComponent implements OnInit {
  video: any = {};

  videoUploader: CustomFileUploader = new CustomFileUploader({
    maxFileSize: 100 * 1024 * 1024,
    allowedMimeType: ['video/mp4', 'video/ogg', 'video/webm'],
    queueLimit: 1,
    url: '',
  });

  thumbnailUploader: CustomFileUploader = new CustomFileUploader({
    maxFileSize: 10 * 1024 * 1024,
    allowedMimeType: ['image/jpeg', 'image/jpg', 'image/png', 'image/bmp'],
    queueLimit: 1,
    url: '',
  });

  @ViewChild('thumbnailCtrl', ) thumbnailCtrl: ElementRef | undefined;

  videoPlayer: ElementRef | undefined;

  @ViewChild('videoPlayer') set content(content: ElementRef) {
    if (content) {
      this.videoPlayer = content;
    }
  }

  @ViewChild('editor') editor: any;
  public isVideoUploadError = false;
  public isThumbnailUploadError = false;
  public videoErrorMessage = '';
  public thumbnailErrorMessage = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public translate: TranslateService,
    private scannerService: VirusScannerService,
    private matDialogRef: MatDialogRef<VideoUploadDialogComponent>,
    private videoService: VideosService,
    private overlayLoaderService: OverlayLoaderService
  ) {
    Object.assign(this.video, data.video);
    if (this.video.isExternal) {
      this.video.externalLink = data.video.videoUrl;
    }
  }

  uploadSelect = `./assets/icons/ico_upload_select_${environment.tenant.trim().toLowerCase()}.svg`;
  link = `./assets/icons/ico_link_${environment.tenant.trim().toLowerCase()}.svg`;
  linkSelect = `./assets/icons/ico_link_select_${environment.tenant.trim().toLowerCase()}.svg`;

  public ngOnInit(): void {
    this.videoUploader.onWhenAddingFileFailed = (item: FileLikeObject, filter, options) => this.onWhenAddingFileFailed(true, item, filter, options);
    this.videoUploader.onAfterAddingFile = (fileItem: FileItem) => this.onAfterAddingVideo(fileItem);

    this.thumbnailUploader.onWhenAddingFileFailed = (item: FileLikeObject, filter, options) => this.onWhenAddingFileFailed(false, item, filter, options);
    this.thumbnailUploader.onAfterAddingFile = (fileItem: FileItem) => this.onAfterAddingThumbnail(fileItem);
  }

  private onWhenAddingFileFailed(isVideoUpload: boolean, item: FileLikeObject, filter: any, options: any): void {
    switch (filter.name) {
      case 'fileSize':
        this.handleFileSizeError(isVideoUpload, options);
        break;
      case 'mimeType':
        this.handleMimeTypeError(isVideoUpload, options);
        break;
      default:
        this.setDefaultErrorMessages();
    }

    this.updateErrorFlags(isVideoUpload);
  }

  private handleFileSizeError(isVideoUpload: boolean, options: any): void {
    const fileSizeErrorMessage: string = this.translate.instant('module.videoUploadDialog.fileSizeErrorMessage');
    const maxFileSize = `${options.maxFileSize / 1024 / 1024}`;
    const errorMessage = StringUtility.replaceTranslation(fileSizeErrorMessage, [maxFileSize]);
    if (isVideoUpload) {
      this.videoErrorMessage = errorMessage;
    } else {
      this.thumbnailErrorMessage = errorMessage;
    }
  }

  private handleMimeTypeError(isVideoUpload: boolean, options: any): void {
    const mimeTypeErrorMessage: string = this.translate.instant('module.videoUploadDialog.mimeTypeErrorMessage');
    const mimeTypes = options.allowedMimeType.join(', ');
    const errorMessage = StringUtility.replaceTranslation(mimeTypeErrorMessage, [mimeTypes]);
    if (isVideoUpload) {
      this.videoErrorMessage = errorMessage;
    } else {
      this.thumbnailErrorMessage = errorMessage;
    }
  }

  private setDefaultErrorMessages(): void {
    const defaultErrorMessage: string = this.translate.instant('module.videoUploadDialog.defaultErrorMessage');
    this.videoErrorMessage = this.thumbnailErrorMessage = defaultErrorMessage;
  }

  private updateErrorFlags(isVideoUpload: boolean): void {
    if (isVideoUpload) {
      this.isVideoUploadError = true;
    } else {
      this.isThumbnailUploadError = true;
    }
  }

  onAfterAddingVideo(item: FileItem) {
    const reader = new FileReader();
    reader.readAsDataURL(item._file);
    reader.onload = (_event) => {
      this.video.videoBlob = reader.result;
    };
    this.video.videoFile = this.videoUploader.queue.map((fileItem) => {
      return fileItem.file.rawFile;
    })[0];
    this.video.videoFileName = item.file.name;
    this.video.videoMimeType = item.file.type;
    this.setMimeType();
    this.isVideoUploadError = false;
  }

  onAfterAddingThumbnail(item: FileItem) {
    const reader = new FileReader();
    reader.readAsDataURL(item._file);
    reader.onload = (_event) => {
      this.video.thumbnailBlob = of(reader.result);
    };
    this.video.thumbnailFile = this.thumbnailUploader.queue.map((fileItem) => {
      return fileItem.file.rawFile;
    })[0];
    this.video.thumbnailFileName = item.file.name;
    this.video.thumbnailDeleted = '0';
    this.isThumbnailUploadError = false;
  }

  removeThumbnail() {
    this.thumbnailUploader.clearQueue();
    this.video.thumbnailFile = null;
    this.video.thumbnailBlob = null;
    this.video.thumbnailDeleted = '1';
    this.isThumbnailUploadError = false;
    this.video.thumbnailFileName = null;
  }

  removeFile() {
    this.videoUploader.clearQueue();
    this.video.videoFile = null;
    this.video.videoBlob = null;
    this.video.videoFileName = null;
    this.video.videoMimeType = null;
    this.video.videoDuration = null;
    this.isVideoUploadError = false;
  }

  save() {
    if (this.video.isExternal) {
      this.removeFile();
    } else {
      this.video.externalLink = null;
      this.video.videoDuration = this.videoPlayer?.nativeElement.duration;
    }
    this.video.description = this.formatHtmlContent(this.video.description);
    this.overlayLoaderService.isLoading = true;
    let save$;
    if (this.video.id) {
      save$ = this.videoService.put(this.video.id, this.video);
    } else {
      save$ = this.videoService.post(this.video);
    }

    save$.subscribe({
      next: (data) => {
        this.matDialogRef.close(true);
        this.overlayLoaderService.isLoading = false;
      },
      error: (err) => {
        this.scannerService.errorChecker(err);
      },
    });
  }

  cancel() {
    this.matDialogRef.close(false);
  }

  isValid() {
    if (
      this.video &&
      this.video.title &&
      trim(this.video.title).length > 0 &&
      this.video.description &&
      trim(this.video.description).length > 0 &&
      ((!this.video.isExternal && !isNil(this.video.videoBlob) && !isNil(this.video.thumbnailBlob)) ||
        (this.video.isExternal && trim(this.video.externalLink).length > 0 && this.isValidURL(this.video.externalLink) && !isNil(this.video.thumbnailBlob)))
    ) {
      return true;
    }

    return false;
  }

  isValidURL(str: string) {
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' +
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
        '((\\d{1,3}\\.){3}\\d{1,3}))' +
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
        '(\\?[;&a-z\\d%_.~+=-]*)?' +
        '(\\#[-a-z\\d_]*)?$',
      'i'
    );
    return !!pattern.test(str);
  }

  setMimeType() {
    if (this.video.videoMimeType) {
      this.video.modifiedMimeType = this.video.videoMimeType;
    }

    if (navigator.userAgent.toLowerCase().indexOf('trident') > -1) {
      this.video.modifiedMimeType += `;codecs="avc1.42E01E, mp4a.40.2"`;
    }
  }

  editorTextChanged(event: any) {
    const limit = 2000;
    if (event.textValue.length > limit) {
      const quill = this.editor.getQuill();
      this.editor.getQuill().deleteText(limit, quill.getLength());
    }
  }

  formatHtmlContent(htmlContent: string) {
    const substring = '<a href="https://';
    const regex = /<a href="(?!http)/g;
    return replace(htmlContent, regex, substring);
  }
}
